/**
 * Utility functions for handling file attachments in email templates
 */

export const fileAttachmentUtils = {
  /**
   * Validate file for email attachment
   */
  validateFile: (file) => {
    const maxSize = 25 * 1024 * 1024; // 25MB limit (common email attachment limit)
    const allowedTypes = [
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      // Archives
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      // Other common types
      'application/json',
      'application/xml',
      'text/html'
    ];

    const errors = [];

    if (file.size > maxSize) {
      errors.push(`File size must be less than 25MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push(`File type not allowed: ${file.type}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Format file size for display
   */
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Get file icon based on file type
   */
  getFileIcon: (mimeType) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📋';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return '🗜️';
    if (mimeType.includes('text')) return '📃';
    return '📎';
  },

  /**
   * Upload file and return attachment metadata
   */
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/attachments', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      return {
        id: result.fileId,
        filename: file.name,
        originalName: file.name,
        mimeType: file.type,
        size: file.size,
        url: result.url,
        uploadedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  },

  /**
   * Delete uploaded file
   */
  deleteFile: async (fileId) => {
    try {
      const response = await fetch(`/api/attachments/${fileId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Delete failed');
      }

      return result;
    } catch (error) {
      console.error('File delete error:', error);
      throw error;
    }
  },

  /**
   * Create attachment object for template storage
   */
  createAttachmentData: (uploadedFile) => {
    return {
      id: uploadedFile.id,
      filename: uploadedFile.filename,
      originalName: uploadedFile.originalName,
      mimeType: uploadedFile.mimeType,
      size: uploadedFile.size,
      url: uploadedFile.url,
      uploadedAt: uploadedFile.uploadedAt
    };
  },

  /**
   * Get total size of all attachments
   */
  getTotalAttachmentSize: (attachments) => {
    return attachments.reduce((total, attachment) => total + (attachment.size || 0), 0);
  },

  /**
   * Check if total attachment size is within limits
   */
  isWithinSizeLimit: (attachments, maxTotalSize = 25 * 1024 * 1024) => {
    const totalSize = fileAttachmentUtils.getTotalAttachmentSize(attachments);
    return {
      isValid: totalSize <= maxTotalSize,
      totalSize,
      maxSize: maxTotalSize,
      remaining: maxTotalSize - totalSize
    };
  }
};